<script setup lang="ts">
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {ref} from 'vue';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useUserStore} from '@/ts/store/user-store';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {analyticsService} from '@/ts/services/analytics-service';
import {shouldEnableAnalytics} from '@/ts/utils/analytics-consent';

const api = useApi();
const userStore = useUserStore();
const logger = useLogger('analytics-anonymized-consent');
const $q = useQuasar();
const {t} = useI18n();

const showModal = defineModel<boolean>({required: true});
const isProcessing = ref(false);

async function handleChoice(approved: boolean): Promise<void> {
  isProcessing.value = true;
  try {
    await api.me.putConsentApproval({
      consentType: 'analytics_anonymized',
      approved,
    });
  } catch (error) {
    logger.error('Failed to submit analytics anonymized consent:', error)();
    $q.notify({
      type: 'negative',
      message: t('general_error'),
    });
    isProcessing.value = false;
    return;
  }

  // The consent is persisted — close the modal so the user can't resubmit.
  showModal.value = false;
  isProcessing.value = false;

  // Best-effort refresh of the store and analytics gating. A failure here is
  // non-blocking: the next authenticated fetch will reconcile consents.
  try {
    const updatedUser = await api.me.getMe();
    userStore.setUserData(updatedUser);
    if (shouldEnableAnalytics(userStore.userData?.consents)) {
      analyticsService.optInCapturing();
    } else {
      analyticsService.optOutCapturing();
    }
  } catch (error) {
    logger.error('Failed to refresh user after analytics consent submit:', error)();
  }
}
</script>

<template>
  <balm-dialog
    v-model="showModal"
    theme="neutral-0"
    type="fit-content"
    :no-x="true"
    :no-backdrop-dismiss="true"
    persistent
    role="dialog"
    aria-labelledby="analytics-consent-modal-title"
    aria-modal="true"
  >
    <div class="analytics-consent-modal">
      <div class="modal-header">
        <h2 id="analytics-consent-modal-title" class="modal-title">
          {{ $t('analytics_anonymized') }}
        </h2>
      </div>

      <div class="modal-content">
        <p class="consent-description">
          {{ $t('i_agree_to_analytics_anonymized') }}
        </p>

        <div class="buttons">
          <balm-btn
            :label="$t('accept')"
            theme="blue"
            paddings="p2-5"
            :loading="isProcessing"
            :disable="isProcessing"
            @click="handleChoice(true)"
          />
          <balm-btn
            :label="$t('decline')"
            theme="grey"
            paddings="p2-5"
            :loading="isProcessing"
            :disable="isProcessing"
            @click="handleChoice(false)"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.analytics-consent-modal {
  padding: var(--Spacing-spacing-4);
  max-height: 90vh;
  overflow-y: auto;

  @include screen-lg {
    min-width: 500px;
    max-width: 700px;
  }
}

.modal-header {
  text-align: center;
}

.modal-title {
  margin-bottom: var(--Spacing-spacing-4);
}

.consent-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  line-height: 1.5;
  margin-bottom: var(--Spacing-spacing-4);
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
  justify-content: center;
  margin-top: var(--Spacing-spacing-6);
  margin-bottom: var(--Spacing-spacing-6);

  :deep(.q-btn) {
    width: 100%;
  }

  @include screen-sm {
    flex-direction: row;

    :deep(.q-btn) {
      width: auto;
    }
  }
}
</style>
